import React, { useState, useEffect } from 'react';
import { 
  Zap, 
  Check, 
  ArrowRight, 
  Database, 
  Search, 
  FileText, 
  Shield, 
  Cpu, 
  HelpCircle, 
  TrendingUp, 
  Star, 
  Map, 
  MessageSquare,
  Lock,
  Loader2,
  Calendar,
  Cloud,
  Briefcase,
  Layers,
  ChevronDown,
  Info,
  Sparkles,
  X,
  Play,
  Square,
  Terminal,
  Sliders,
  Building,
  Users,
  CheckCircle2,
  ArrowUpRight,
  Settings,
  Globe,
  Award,
  Bell,
  RefreshCw,
  Clock,
  Target,
  Copy,
  ExternalLink,
  AlertTriangle
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { auth, signInWithPopup, googleProvider, currentAuthInfo, detectEnvironmentAuth, setCustomAuthDomain } from '../firebase';
import { Card, Button, cn } from './SaaSUIHelper';

const SANDBOX_PRESETS = {
  california: {
    logs: [
      "[Aura Scraper] Initializing Playwright session for California Cal eProcure...",
      "[Aura Scraper] Searching active solicitations matching NIGP 208-11...",
      "[Policy Auditor] Verifying CDT 15% Small Business / DVBE preference rules...",
      "[Gemini Copilot] Synthesizing past performance narrative and drafting proposal..."
    ],
    markdown: `### Executive Summary: California CDT Cloud Integration
**Target Opportunity**: CDT-2026-9402 Cloud System Integration Support  
**Issuing Agency**: California Department of Technology (CDT)  
**Preference Score**: 98% Match (DVBE / Small Business Set-Aside Eligible)

#### 1. Proposed Technical Architecture
Our solution leverages a multi-region high-availability cloud architecture compliant with California Information Security Office (CISO) standards and SAM Chapter 5300 security controls.

#### 2. Compliance & Mandates
- **State Policy**: Complies with CA Technology Recovery Plan requirements.
- **Subcontracting**: Meets statutory 25% Small Business participation goal.
- **Estimated Timeline**: 6-month phased rollout with zero-downtime transition.`
  },
  texas: {
    logs: [
      "[Aura Scraper] Connecting to Texas SmartBuy ESBD...",
      "[Aura Scraper] Filtering HVAC maintenance & facilities opportunities...",
      "[Policy Auditor] Checking Texas Section 12-B and HUB utilization goals...",
      "[Gemini Copilot] Generating technical work breakdown and pricing sheet..."
    ],
    markdown: `### Executive Summary: Texas Facilities HVAC Optimization
**Target Opportunity**: TX-FAC-8803 HVAC Maintenance & System Optimization  
**Issuing Agency**: Texas State University System  
**Preference Score**: 89% Match (Texas Historically Underutilized Business - HUB)

#### 1. Preventive Maintenance & Air Quality Control
Deployment of automated IoT vibration sensors and BACnet-integrated energy monitoring across campus facilities.

#### 2. HUB Participation Plan
- **Primary Contractor**: 70% direct HVAC technical execution.
- **Certified HUB Partner**: 30% electrical and environmental monitoring controls.`
  },
  florida: {
    logs: [
      "[Aura Scraper] Querying Florida Vendor Information Portal (MFMP)...",
      "[Aura Scraper] Extracting active IT consulting & staff augmentation bids...",
      "[Policy Auditor] Verifying Florida DMS General Contract Conditions...",
      "[Gemini Copilot] Drafting key personnel matrix and deliverables schedule..."
    ],
    markdown: `### Executive Summary: Florida IT Staff Augmentation
**Target Opportunity**: FL-DMS-2026-01 Enterprise Data Analytics  
**Issuing Agency**: Florida Department of Management Services  
**Preference Score**: 94% Match

#### 1. Resource Delivery Methodology
Rapid onboarding pipeline delivering certified cloud data architects within 5 business days of task order issuance.

#### 2. Quality Assurance & SLA Guarantees
- 99.9% sprint commitment reliability.
- Bi-weekly sprint velocity and compliance audits.`
  }
};

interface SaaSLandingPageProps {
  onLoginSuccess: () => void;
}

export const SaaSLandingPage: React.FC<SaaSLandingPageProps> = ({ onLoginSuccess }) => {
  // Navigation & Tabs
  const [activeNavTab, setActiveNavTab] = useState<'product' | 'solutions' | 'resources' | 'pricing' | 'home'>('home');
  const [activeSolutionTab, setActiveSolutionTab] = useState<'sub' | 'prime' | 'mwbe'>('sub');
  const [activeMockTab, setActiveMockTab] = useState<'scanner' | 'draftsman' | 'auditor'>('scanner');

  // Sandbox States
  const [selectedSandboxPreset, setSelectedSandboxPreset] = useState<'california' | 'texas' | 'florida'>('california');
  const [isAgentRunning, setIsAgentRunning] = useState<boolean>(false);
  const [isRunningCustom, setIsRunningCustom] = useState<boolean>(false);
  const [agentLogs, setAgentLogs] = useState<string[]>([]);
  const [streamedText, setStreamedText] = useState<string>('');
  const [customPlaygroundQuery, setCustomPlaygroundQuery] = useState<string>('');
  
  // SaaS States (carried over from original)
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [selectedPlanForCheckout, setSelectedPlanForCheckout] = useState<any | null>(null);
  const [checkoutStep, setCheckoutStep] = useState<'plan' | 'paypal_auth' | 'paypal_processing' | 'success'>('plan');
  const [paypalEmail, setPaypalEmail] = useState('');
  const [paypalPassword, setPaypalPassword] = useState('');
  const [faqOpen, setFaqOpen] = useState<Record<number, boolean>>({});
  const [roiVolume, setRoiVolume] = useState(10); // bids per month
  const [isSigningIn, setIsSigningIn] = useState(false);

  // Demo Scheduler Modal States
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [oauthErrorModalOpen, setOauthErrorModalOpen] = useState(false);
  const [copiedRedirectUri, setCopiedRedirectUri] = useState(false);
  const [demoFormData, setDemoFormData] = useState({
    name: '',
    email: '',
    company: '',
    revenue: '$1M - $5M',
    category: 'Information Technology (NAICS 541511)',
    state: 'California'
  });
  const [demoStep, setDemoStep] = useState<'form' | 'success'>('form');

  // Authenticate Google OAuth handler (carried over)
  const handleGoogleLogin = async (selectedTierPreset?: string) => {
    setIsSigningIn(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      if (result.user) {
        if (selectedTierPreset) {
          localStorage.setItem('pending_tier_upgrade', selectedTierPreset);
        }
        onLoginSuccess();
      }
    } catch (error: any) {
      if (
        error?.code === 'auth/popup-closed-by-user' ||
        error?.code === 'auth/cancelled-popup-request' ||
        (typeof error?.message === 'string' && error.message.includes('popup-closed-by-user'))
      ) {
        console.warn('Google sign-in popup dismissed by user.');
        return;
      }
      console.error("Google authentication failed:", error);
      setOauthErrorModalOpen(true);
    } finally {
      setIsSigningIn(false);
    }
  };

  // Run Sandbox Simulation
  const executeSandboxAgent = (presetKey: 'california' | 'texas' | 'florida') => {
    if (isAgentRunning) return;
    setIsAgentRunning(true);
    setAgentLogs([]);
    setStreamedText('');
    setIsRunningCustom(false);

    const preset = SANDBOX_PRESETS[presetKey];
    let logIndex = 0;

    // Simulate step-by-step logs
    const logInterval = setInterval(() => {
      if (logIndex < preset.logs.length) {
        setAgentLogs(prev => [...prev, preset.logs[logIndex]]);
        logIndex++;
      } else {
        clearInterval(logInterval);
        // Start streaming Markdown
        let charIndex = 0;
        const targetText = preset.markdown;
        const streamInterval = setInterval(() => {
          if (charIndex < targetText.length) {
            setStreamedText(targetText.substring(0, charIndex + 3));
            charIndex += 4;
          } else {
            setStreamedText(targetText);
            clearInterval(streamInterval);
            setIsAgentRunning(false);
          }
        }, 15);
      }
    }, 700);
  };

  // Run Custom User Query in Sandbox
  const executeCustomSandbox = () => {
    if (isAgentRunning || !customPlaygroundQuery.trim()) return;
    setIsAgentRunning(true);
    setAgentLogs([]);
    setStreamedText('');
    setIsRunningCustom(true);

    const customLogs = [
      `[Aura Scraper] Parsing custom query: "${customPlaygroundQuery}"...`,
      "[Aura Scraper] Cross-referencing 50 state databases for classifications...",
      "[Aura Locker] Indexing Past Performance documents in Locker...",
      "[Aura Auditor] Triggering state-specific policy compliance scripts...",
      "[Gemini LLM] Sourcing context window & grounding responses using state regulations..."
    ];

    let logIndex = 0;
    const logInterval = setInterval(() => {
      if (logIndex < customLogs.length) {
        setAgentLogs(prev => [...prev, customLogs[logIndex]]);
        logIndex++;
      } else {
        clearInterval(logInterval);
        
        // Generate customized Markdown response based on query keywords
        const queryLower = customPlaygroundQuery.toLowerCase();
        let topic = "Custom Sourcing Support";
        let classification = "General Commercial";
        if (queryLower.includes("solar") || queryLower.includes("energy")) {
          topic = "Renewable Solar Energy Sourcing";
          classification = "Solar & Energy (NIGP 290-82)";
        } else if (queryLower.includes("construction") || queryLower.includes("build")) {
          topic = "State Building & Concrete Refurbishing";
          classification = "Construction & Engineering (NAICS 236220)";
        } else if (queryLower.includes("it") || queryLower.includes("cloud") || queryLower.includes("software")) {
          topic = "Enterprise Cloud Computing Integration";
          classification = "Information Technology (NAICS 541511)";
        }

        const generatedMarkdown = `### AUTONOMOUS PROPOSAL PROTOTYPE
**Reference Request:** ${customPlaygroundQuery}
**Sector Category:** ${classification}
**Status:** Grounded with Gemini AI

#### 1. Engineered Technical Methodology
Based on custom instructions, RFXpress outlines a robust execution matrix to solve the core requirements:
* **Advanced Solution Architecture:** Fully aligned with standard commercial policies.
* **Continuous Operational Oversight:** 24/7 telemetry monitoring to secure strict state SLAs.

#### 2. Sourcing Preferences & Compliance Audits
* **Supplier Preference:** Local enterprise multipliers active.
* **Goal Credit Integration:** MWBE participation targets set at a recommended **22.0%**. Certified partners will be pulled automatically into your corporate locker.`;

        let charIndex = 0;
        const streamInterval = setInterval(() => {
          if (charIndex < generatedMarkdown.length) {
            setStreamedText(generatedMarkdown.substring(0, charIndex + 3));
            charIndex += 4;
          } else {
            setStreamedText(generatedMarkdown);
            clearInterval(streamInterval);
            setIsAgentRunning(false);
          }
        }, 15);
      }
    }, 600);
  };

  // Auto-run California simulation on load
  useEffect(() => {
    executeSandboxAgent('california');
  }, []);

  const pricingPlans = [
    {
      name: 'Starter Tier',
      tagline: 'Ideal for small subcontractors starting in state procurement.',
      monthlyPrice: 49,
      annualPrice: 39,
      features: [
        'Single-State portal tracking',
        '5 AI RFX responses per month',
        'Basic local company profile management',
        'Email alerts for matching bids',
        '1 User seat included',
        'Standard dashboard tracking'
      ],
      color: 'border-slate-250 text-slate-900',
      badge: 'Subcontractor Essential',
      popular: false,
      tierId: 'starter'
    },
    {
      name: 'Professional Tier',
      tagline: 'Perfect for growing contractors targeting multi-state deals.',
      monthlyPrice: 149,
      annualPrice: 119,
      features: [
        'All 50 States portal tracking (Crawlers)',
        'Unlimited AI RFX Response generations',
        'Access to full RFX Procurement Database',
        'Google Workspace Integration (Docs, Drive)',
        'Google Calendar & Reminders scheduler',
        'Advanced compliance checklists',
        '3 User seats included',
        'Priority email & chat support'
      ],
      color: 'border-indigo-500 ring-2 ring-indigo-500/20 text-indigo-950',
      badge: 'Best Value / Most Popular',
      popular: true,
      tierId: 'pro'
    },
    {
      name: 'Enterprise Tier',
      tagline: 'For prime vendors requiring absolute compliance & scale.',
      monthlyPrice: 499,
      annualPrice: 399,
      features: [
        'Custom web crawler agents on-demand',
        'Dedicated Gemini API throughput allocation',
        'Interactive Win-Rate Predictive Analytics',
        'Past Performance compliance optimizer',
        'White-labeled bid response exporting',
        'Unlimited User seats',
        'Dedicated account director / analyst support',
        'Full SLA guaranteed response logs'
      ],
      color: 'border-slate-800 text-slate-950',
      badge: 'Prime Contractor Scale',
      popular: false,
      tierId: 'enterprise'
    }
  ];

  const testimonials = [
    {
      quote: "RFXpress completely revolutionized our bidding process. We went from spending 30 hours per RFX proposal to under 2 hours, winning a CA state transit contract worth $1.2M within 3 weeks of upgrading to Pro.",
      author: "Marcus Vance",
      role: "VP of Business Development, TransitCore Solutions",
      rating: 5,
      metric: "Won $1.2M Contract",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=100"
    },
    {
      quote: "The multi-state crawler database identified matching solicitations in New York that we never would have discovered manually. The PayPal billing setup was fast, secure, and the tax billing invoices sync flawlessly.",
      author: "Sarah Jenkins",
      role: "Procurement Lead, AeroGov Consulting",
      rating: 5,
      metric: "Tracked 50 States",
      avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=100"
    },
    {
      quote: "Using Aura's compliance checks, we qualified for prime MWBE set-asides in Texas. The system automatically checked our HUB goals and suggested the ideal sub-partners, leading to a major municipal contract win.",
      author: "David Chen",
      role: "Founder, IntegraTech Group",
      rating: 5,
      metric: "Sourced 23% HUB Goal",
      avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&q=80&w=100"
    }
  ];

  const faqs = [
    {
      question: "How does the All 50 States Crawler database operate?",
      answer: "Our automated system constantly crawls public sector portals, state treasuries, and agency bidding platforms (including California Cal eProcure, Texas Comptroller, and Florida e-Procurement). It aggregates description strings, compliance details, and line-item tables into a structured Firestore collection so you can access them instantly."
    },
    {
      question: "Is there an interactive trial before payment is processed?",
      answer: "Yes! You can sign up with any Google Account to access the free preview of our dashboard. If you need pro features—such as full Google Workspace document compilation or unlimited Gemini proposal generations—you can securely upgrade to Pro or Enterprise at any time via PayPal."
    },
    {
      question: "Are the generated AI RFX responses legally compliant?",
      answer: "Absolutely. RFXpress evaluates the exact state statutes, threshold rules, and past performance indices cached locally. The Gemini model runs a secure technical fit assessment, ensuring compliance declarations and line-item bid estimates are grounded in documented state specs."
    },
    {
      question: "How secure is the PayPal billing portal?",
      answer: "RFXpress handles payments directly through PayPal's secure gateway APIs. Your credentials never touch our servers, and all active subscription receipts, invoices, and billing histories are securely compiled to guarantee absolute audit readiness."
    }
  ];

  const toggleFaq = (index: number) => {
    setFaqOpen(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // ROI Calculator Calculations
  const estimatedHoursSaved = roiVolume * 24; // 24 hours saved per bid
  const estimatedCostSaved = estimatedHoursSaved * 75; // $75/hr average billing rate

  const handleOpenCheckout = (plan: any) => {
    setSelectedPlanForCheckout(plan);
    setCheckoutStep('paypal_auth');
  };

  const executeSimulatedPayment = () => {
    if (!paypalEmail) {
      alert("Please provide a valid PayPal email.");
      return;
    }
    setCheckoutStep('paypal_processing');
    
    setTimeout(() => {
      setCheckoutStep('success');
    }, 2000);
  };

  const finalizeUpgradeAfterSuccess = () => {
    const authUser = auth.currentUser;
    if (authUser) {
      localStorage.setItem('pending_tier_upgrade', selectedPlanForCheckout.tierId);
      onLoginSuccess();
    } else {
      handleGoogleLogin(selectedPlanForCheckout.tierId);
    }
    setSelectedPlanForCheckout(null);
    setCheckoutStep('plan');
  };

  const handleDemoSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!demoFormData.name || !demoFormData.email) {
      alert("Please fill out your name and work email.");
      return;
    }
    setDemoStep('success');
  };

  const closeDemoModal = () => {
    setIsDemoModalOpen(false);
    setDemoStep('form');
    setDemoFormData({
      name: '',
      email: '',
      company: '',
      revenue: '$1M - $5M',
      category: 'Information Technology (NAICS 541511)',
      state: 'California'
    });
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] text-[#0A0B10] font-sans antialiased selection:bg-indigo-600 selection:text-white scroll-smooth" id="saas-landing-root">
      
      {/* ENTERPRISE STICKY HEADER */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-slate-200/80 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 bg-[#0B0C10] rounded-xl flex items-center justify-center shadow-lg shadow-indigo-100/10">
            <Zap className="text-indigo-400 w-5 h-5 animate-pulse" />
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <span className="font-extrabold text-xl text-[#0A0B10] tracking-tight">RFXpress</span>
              <span className="text-[9px] font-bold text-white bg-indigo-600 px-1.5 py-0.5 rounded uppercase tracking-wider">Aura AI</span>
            </div>
            <p className="text-[8px] text-gray-400 font-bold tracking-widest uppercase">Autonomous Bidding Platform</p>
          </div>
        </div>

        {/* Dynamic Nav Tabs with megamenu/solutions structure */}
        <nav className="hidden lg:flex items-center gap-8 text-xs font-semibold text-slate-600">
          <a href="#product-section" className="hover:text-indigo-600 transition-colors flex items-center gap-1">
            Product <ChevronDown className="w-3 h-3 text-slate-400" />
          </a>
          <a href="#solutions-section" className="hover:text-indigo-600 transition-colors flex items-center gap-1">
            Solutions <ChevronDown className="w-3 h-3 text-slate-400" />
          </a>
          <a href="#playground-section" className="hover:text-indigo-600 transition-colors">Aura Console</a>
          <a href="#roi-calculator" className="hover:text-indigo-600 transition-colors">ROI Estimator</a>
          <a href="#pricing" className="hover:text-indigo-600 transition-colors">Pricing Plans</a>
          <a href="#faqs" className="hover:text-indigo-600 transition-colors">FAQ</a>
        </nav>

        <div className="flex items-center gap-3">
          <button 
            onClick={() => handleGoogleLogin()}
            disabled={isSigningIn}
            className="px-4 py-2 text-xs font-bold text-slate-700 bg-white border border-slate-200 rounded-lg hover:border-slate-300 transition-all cursor-pointer shadow-sm flex items-center gap-1.5"
          >
            {isSigningIn ? <Loader2 className="w-3.5 h-3.5 animate-spin text-indigo-600" /> : <Shield className="w-3.5 h-3.5 text-slate-400" />}
            Sign In with Google
          </button>
          <button 
            onClick={() => setIsDemoModalOpen(true)}
            className="px-4 py-2 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-all cursor-pointer shadow-md shadow-indigo-100"
          >
            Request Demo
          </button>
        </div>
      </header>

      {/* COMPREHENSIVE HERO SECTION (Cloned from Procure.ai's high-fidelity layout) */}
      <section className="relative py-24 px-6 overflow-hidden bg-gradient-to-b from-white via-[#FAFBFD] to-[#F3F5F9]">
        {/* Subtle grid lines in background */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#eef2f6_1px,transparent_1px),linear-gradient(to_bottom,#eef2f6_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] opacity-60" />
        
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative z-10">
          
          {/* HERO LEFT COLUMN */}
          <div className="lg:col-span-7 space-y-8 text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-50 border border-indigo-100 rounded-full text-[10px] font-bold text-indigo-700 uppercase tracking-widest">
              <Sparkles className="w-3 h-3 text-indigo-500 animate-pulse" />
              Meet Aura — Autonomous Proposal Agent 2.1
            </div>

            <div className="space-y-4">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-[#0A0B10] tracking-tight leading-[1.05] font-sans">
                Autonomous Bidding <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
                  for Public Procurement
                </span>
              </h1>
              <p className="text-slate-600 text-sm md:text-base leading-relaxed max-w-2xl font-normal">
                RFXpress automates the end-to-end public sector proposal pipeline. Scrape matching bids across 50 state portals, securely inject historical past-performances, audit strict state compliance guidelines, and compile high-scoring drafts in minutes with Gemini AI.
              </p>
            </div>

            {/* Quick action buttons & integrations count */}
            <div className="flex flex-wrap gap-3">
              <button 
                onClick={() => setIsDemoModalOpen(true)}
                className="flex items-center justify-center gap-2 px-6 py-4 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl shadow-lg shadow-indigo-150 transition-all cursor-pointer"
              >
                Schedule Live Product Tour
                <ArrowRight className="w-4 h-4" />
              </button>
              <button 
                onClick={() => handleGoogleLogin()}
                className="flex items-center justify-center gap-2.5 px-6 py-4 text-xs font-bold text-slate-800 bg-white border border-slate-200 hover:border-slate-300 rounded-xl shadow-sm transition-all cursor-pointer"
              >
                <img src="https://images.unsplash.com/photo-1573804633927-bfcbcd909acd?auto=format&fit=crop&q=80&w=30" className="w-4 h-4 object-contain" alt="Google logo" />
                Sign Up with Google Workspace
              </button>
            </div>

            {/* Key Metrics Row */}
            <div className="grid grid-cols-3 gap-6 pt-8 border-t border-slate-200">
              <div className="space-y-1">
                <h4 className="text-2xl md:text-3xl font-extrabold text-indigo-600 font-sans">50 States</h4>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Scraped Continuously</p>
              </div>
              <div className="space-y-1">
                <h4 className="text-2xl md:text-3xl font-extrabold text-[#0B0C10] font-sans">93% Faster</h4>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Proposal Sourcing</p>
              </div>
              <div className="space-y-1">
                <h4 className="text-2xl md:text-3xl font-extrabold text-emerald-600 font-sans">$45M+ Won</h4>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">By Autonomous Members</p>
              </div>
            </div>
          </div>

          {/* HERO RIGHT COLUMN (Sleek Procure.ai Dashboard Clone Layout) */}
          <div className="lg:col-span-5 relative mt-6 lg:mt-0">
            {/* Soft background glows */}
            <div className="absolute -inset-10 bg-indigo-200/20 rounded-[40px] blur-3xl opacity-60 transform rotate-12 scale-90" />
            <div className="absolute -inset-4 bg-purple-200/10 rounded-[40px] blur-3xl opacity-40 transform -rotate-12 scale-95" />

            <Card className="border-slate-200 shadow-2xl relative bg-white overflow-hidden rounded-2xl">
              {/* Fake Application Window Header */}
              <div className="bg-[#0B0C10] px-4 py-3.5 flex justify-between items-center text-white">
                <div className="flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-500/80" />
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/80" />
                  </div>
                  <span className="text-[10px] text-slate-400 font-bold tracking-widest uppercase pl-2 border-l border-slate-800">
                    AURA_ENGINE_V2.1
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-[9px] font-bold text-emerald-400">ONLINE</span>
                </div>
              </div>

              {/* Inner Dashboard Tabs */}
              <div className="border-b border-slate-100 bg-slate-50 flex text-[11px] font-bold text-slate-500">
                <button 
                  onClick={() => setActiveMockTab('scanner')}
                  className={cn(
                    "flex-1 py-3 px-2 text-center border-b-2 transition-colors cursor-pointer",
                    activeMockTab === 'scanner' ? "border-indigo-600 text-indigo-600 bg-white" : "border-transparent hover:text-slate-800"
                  )}
                >
                  1. Sourcing Scanner
                </button>
                <button 
                  onClick={() => setActiveMockTab('draftsman')}
                  className={cn(
                    "flex-1 py-3 px-2 text-center border-b-2 transition-colors cursor-pointer",
                    activeMockTab === 'draftsman' ? "border-indigo-600 text-indigo-600 bg-white" : "border-transparent hover:text-slate-800"
                  )}
                >
                  2. Proposal Writer
                </button>
                <button 
                  onClick={() => setActiveMockTab('auditor')}
                  className={cn(
                    "flex-1 py-3 px-2 text-center border-b-2 transition-colors cursor-pointer",
                    activeMockTab === 'auditor' ? "border-indigo-600 text-indigo-600 bg-white" : "border-transparent hover:text-slate-800"
                  )}
                >
                  3. Policy Auditor
                </button>
              </div>

              {/* Dynamic Interactive Tab Content */}
              <div className="p-5 min-h-[280px] bg-white flex flex-col justify-between">
                <AnimatePresence mode="wait">
                  {activeMockTab === 'scanner' && (
                    <motion.div 
                      key="scanner"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="space-y-4 text-left"
                    >
                      <div className="flex justify-between items-center">
                        <span className="text-[9px] font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded uppercase">State Crawler Scan</span>
                        <span className="text-[10px] text-slate-400 font-mono">Synced: Just Now</span>
                      </div>
                      <div className="space-y-2.5">
                        <div className="p-3 bg-slate-50 rounded-xl border border-slate-150 space-y-1">
                          <div className="flex justify-between items-start">
                            <h5 className="text-xs font-extrabold text-slate-900 truncate max-w-[200px]">CDT-2026-9402: Cloud System Integration Support</h5>
                            <span className="text-[9px] font-bold text-indigo-700 bg-indigo-50 px-1.5 rounded font-mono">98% Match</span>
                          </div>
                          <p className="text-[10px] text-slate-500">California Dept of Technology | Due in 14 days</p>
                          <div className="flex gap-1.5 pt-1">
                            <span className="text-[8px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-mono">NIGP 208-11</span>
                            <span className="text-[8px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded font-mono">MWBE Sourced</span>
                          </div>
                        </div>

                        <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 space-y-1 opacity-75">
                          <div className="flex justify-between items-start">
                            <h5 className="text-xs font-bold text-slate-800 truncate max-w-[200px]">TX-FAC-8803: HVAC Maintenance & System Optimization</h5>
                            <span className="text-[9px] font-bold text-indigo-700 bg-indigo-50 px-1.5 rounded font-mono">89% Match</span>
                          </div>
                          <p className="text-[10px] text-slate-400">Texas State University | Due in 21 days</p>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {activeMockTab === 'draftsman' && (
                    <motion.div 
                      key="draftsman"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="space-y-3.5 text-left"
                    >
                      <div className="flex justify-between items-center">
                        <span className="text-[9px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded uppercase">Gemini Response Copilot</span>
                        <span className="text-[10px] text-slate-400 font-mono">Locker Sync Active</span>
                      </div>
                      <div className="bg-slate-900 text-emerald-400 font-mono text-[10px] p-3 rounded-lg border border-slate-800 h-36 overflow-y-auto space-y-1.5">
                        <p className="text-slate-500">// Pulling credentials: 'TransitCore Schedulers'</p>
                        <p className="text-white">### Technical Methodology & Execution</p>
                        <p className="text-emerald-300">We propose a scalable, containerized cloud infrastructure service to support CDT mission-critical workloads...</p>
                        <div className="w-1.5 h-3.5 bg-emerald-400 inline-block animate-pulse ml-0.5" />
                      </div>
                      <p className="text-[9px] text-slate-400 italic">Auto-generates compliant summaries, work plans, and QA checklists grounded in past contract success.</p>
                    </motion.div>
                  )}

                  {activeMockTab === 'auditor' && (
                    <motion.div 
                      key="auditor"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="space-y-4 text-left"
                    >
                      <div className="flex justify-between items-center">
                        <span className="text-[9px] font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded uppercase">Smart Policy Engine</span>
                        <span className="text-[10px] text-slate-400 font-mono">Rule Index: 2026.4</span>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-xs p-2.5 bg-emerald-50/50 border border-emerald-100 rounded-lg">
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                            <span className="font-bold text-slate-800 text-[11px]">California DVBE Credit Qualified</span>
                          </div>
                          <span className="font-mono text-[10px] text-emerald-700 font-bold">Passed</span>
                        </div>
                        <div className="flex items-center justify-between text-xs p-2.5 bg-emerald-50/50 border border-emerald-100 rounded-lg">
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                            <span className="font-bold text-slate-800 text-[11px]">HUB Set-Aside Subcontractor Goals</span>
                          </div>
                          <span className="font-mono text-[10px] text-emerald-700 font-bold">23.5% Committed</span>
                        </div>
                        <div className="flex items-center justify-between text-xs p-2.5 bg-amber-50/50 border border-amber-100 rounded-lg">
                          <div className="flex items-center gap-2">
                            <Info className="w-4 h-4 text-amber-600" />
                            <span className="font-bold text-slate-800 text-[11px]">Local Preference Address Match</span>
                          </div>
                          <span className="font-mono text-[10px] text-amber-700 font-bold">5% Buffer</span>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-500">
                  <span className="flex items-center gap-1.5">
                    <Cloud className="w-3.5 h-3.5 text-indigo-500" />
                    Google Workspace Live Connected
                  </span>
                  <a href="#playground-section" className="text-indigo-600 hover:text-indigo-700 font-bold flex items-center gap-0.5 cursor-pointer">
                    Open Live Sandbox
                    <ArrowUpRight className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </Card>
          </div>

        </div>
      </section>

      {/* HORIZONTAL LOGO SHOWCASE (Fading Edges, Monochrome Style) */}
      <section className="py-10 border-y border-slate-200 bg-white relative overflow-hidden">
        <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-white to-transparent z-10" />
        <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-white to-transparent z-10" />
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6">Trusted by High-Growth Contractors, Prime Vendors, & MWBE Subcontractors</p>
          <div className="flex flex-wrap justify-center items-center gap-x-12 gap-y-6 opacity-65 grayscale hover:grayscale-0 transition-all">
            <div className="flex items-center gap-2">
              <Building className="w-4 h-4 text-slate-800" />
              <span className="font-bold text-xs tracking-tight text-slate-800 uppercase">TransitCore Solutions</span>
            </div>
            <div className="flex items-center gap-2">
              <Layers className="w-4 h-4 text-slate-800" />
              <span className="font-bold text-xs tracking-tight text-slate-800 uppercase">AeroGov Consulting</span>
            </div>
            <div className="flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-slate-800" />
              <span className="font-bold text-xs tracking-tight text-slate-800 uppercase">BuildCorp Infra</span>
            </div>
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-slate-800" />
              <span className="font-bold text-xs tracking-tight text-slate-800 uppercase">FederalSys Global</span>
            </div>
            <div className="flex items-center gap-2">
              <Award className="w-4 h-4 text-slate-800" />
              <span className="font-bold text-xs tracking-tight text-slate-800 uppercase">GovSystems Certified</span>
            </div>
          </div>
        </div>
      </section>

      {/* THE BIDDING REVOLUTION (Legacy vs Autonomous Bidding Grid) */}
      <section className="py-24 px-6 bg-white" id="product-section">
        <div className="max-w-6xl mx-auto space-y-16">
          <div className="text-center space-y-3">
            <span className="text-[10px] font-black tracking-widest text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-full uppercase">THE PROCUREMENT PARADIGM SHIFT</span>
            <h2 className="text-3xl font-black text-slate-900 tracking-tight">AI Agents That Draft & Audit, Not Just Search</h2>
            <p className="text-slate-500 text-xs max-w-lg mx-auto">
              Compare the overhead of manual public bidding portals with RFXpress's unified autonomous pipeline.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* Legacy Bidding Column */}
            <div className="p-8 rounded-2xl bg-slate-50 border border-slate-200 space-y-6">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-rose-50 text-rose-600 rounded-xl">
                  <Clock className="w-5 h-5 animate-pulse" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 text-sm">Legacy Sourcing Operations</h3>
                  <p className="text-[10px] text-rose-600 font-bold uppercase tracking-wider">Manual | Dispersed | High Friction</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex gap-3 text-xs text-slate-600 items-start">
                  <div className="w-5 h-5 rounded-full bg-rose-100 flex items-center justify-center shrink-0 mt-0.5 text-rose-600 font-bold">×</div>
                  <p><strong>Dispersed Portals:</strong> Checking dozens of separate state registries daily (Cal eProcure, NY State, TX Comptroller), missing high-yield opportunities due to fragmented notifications.</p>
                </div>
                <div className="flex gap-3 text-xs text-slate-600 items-start">
                  <div className="w-5 h-5 rounded-full bg-rose-100 flex items-center justify-center shrink-0 mt-0.5 text-rose-600 font-bold">×</div>
                  <p><strong>Repetitive Copy-Pasting:</strong> Sifting through multiple historical proposal PDFs manually, copy-pasting corporate boilerplates, leading to formatting typos and legacy errors.</p>
                </div>
                <div className="flex gap-3 text-xs text-slate-600 items-start">
                  <div className="w-5 h-5 rounded-full bg-rose-100 flex items-center justify-center shrink-0 mt-0.5 text-rose-600 font-bold">×</div>
                  <p><strong>Manual Policy Auditing:</strong> Manually reviewing MWBE set-aside rules, bond limits, and local preference ratios across varying regional codes—introducing critical compliance liabilities.</p>
                </div>
                <div className="flex gap-3 text-xs text-slate-600 items-start">
                  <div className="w-5 h-5 rounded-full bg-rose-100 flex items-center justify-center shrink-0 mt-0.5 text-rose-600 font-bold">×</div>
                  <p><strong>Tight Timelines:</strong> Spending up to 30 hours per proposal response. High workloads result in bidding for fewer projects, limiting firm growth.</p>
                </div>
              </div>
            </div>

            {/* Autonomous Bidding Column */}
            <div className="p-8 rounded-2xl bg-indigo-50/20 border border-indigo-100 space-y-6 ring-2 ring-indigo-500/10">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-indigo-50 text-indigo-600 rounded-xl">
                  <Cpu className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 text-sm">RFXpress Autonomous Sourcing</h3>
                  <p className="text-[10px] text-indigo-600 font-bold uppercase tracking-wider">Automated | Centralized | Powered by Aura AI</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex gap-3 text-xs text-slate-700 items-start">
                  <Check className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5 bg-emerald-50 rounded-full p-0.5" />
                  <p><strong>Continuous Scrapers:</strong> Advanced automated agents scrape all 50 states and cache matching solicitations by NAICS & NIGP instantly inside a centralized Firestore collection.</p>
                </div>
                <div className="flex gap-3 text-xs text-slate-700 items-start">
                  <Check className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5 bg-emerald-50 rounded-full p-0.5" />
                  <p><strong>Boilerplate Locker Ingestion:</strong> Secure locker preserves your corporate past performance documents, certificates, and pricing structures, immediately feeding them into Gemini proposals.</p>
                </div>
                <div className="flex gap-3 text-xs text-slate-700 items-start">
                  <Check className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5 bg-emerald-50 rounded-full p-0.5" />
                  <p><strong>Smart Compliance Audits:</strong> AI Engine automatically cross-references and validates state-specific small business targets and local evaluation buffers so your bid remains bulletproof.</p>
                </div>
                <div className="flex gap-3 text-xs text-slate-700 items-start">
                  <Check className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5 bg-emerald-50 rounded-full p-0.5" />
                  <p><strong>Workspace Live Compile:</strong> Generates hyper-compliant responses (technical methodology, QA, covers) in under 2 hours, compiling directly to real Google Docs.</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* DETAILED SOLUTIONS BY ROLE (Tabs styled like Procure.ai) */}
      <section className="py-24 px-6 bg-slate-50 border-t border-slate-200" id="solutions-section">
        <div className="max-w-6xl mx-auto space-y-12">
          
          <div className="text-center space-y-3">
            <span className="text-[10px] font-black tracking-widest text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-full uppercase">OPTIMIZED SOLUTION ENGINES</span>
            <h2 className="text-3xl font-black text-slate-900 tracking-tight">Tailored Sourcing for Any Government Contractor</h2>
            <p className="text-slate-500 text-xs max-w-md mx-auto">
              Select your organization size or business category to explore custom bid automation solutions.
            </p>
          </div>

          {/* Interactive Role Tabs */}
          <div className="flex justify-center border-b border-slate-200 max-w-xl mx-auto">
            <button 
              onClick={() => setActiveSolutionTab('sub')}
              className={cn(
                "flex-1 py-3 px-4 text-xs font-bold border-b-2 transition-all cursor-pointer",
                activeSolutionTab === 'sub' ? "border-indigo-600 text-indigo-600 font-extrabold" : "border-transparent text-slate-400 hover:text-slate-700"
              )}
            >
              For Subcontractors
            </button>
            <button 
              onClick={() => setActiveSolutionTab('prime')}
              className={cn(
                "flex-1 py-3 px-4 text-xs font-bold border-b-2 transition-all cursor-pointer",
                activeSolutionTab === 'prime' ? "border-indigo-600 text-indigo-600 font-extrabold" : "border-transparent text-slate-400 hover:text-slate-700"
              )}
            >
              For Prime Contractors
            </button>
            <button 
              onClick={() => setActiveSolutionTab('mwbe')}
              className={cn(
                "flex-1 py-3 px-4 text-xs font-bold border-b-2 transition-all cursor-pointer",
                activeSolutionTab === 'mwbe' ? "border-indigo-600 text-indigo-600 font-extrabold" : "border-transparent text-slate-400 hover:text-slate-700"
              )}
            >
              For MWBE Set-Asides
            </button>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
            <AnimatePresence mode="wait">
              {activeSolutionTab === 'sub' && (
                <motion.div 
                  key="sub"
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -15 }}
                  className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center"
                >
                  <div className="lg:col-span-7 space-y-5 text-left">
                    <h3 className="text-xl font-extrabold text-slate-900">Dominate Local Bidding with Speed & High Margins</h3>
                    <p className="text-xs text-slate-500 leading-relaxed">
                      Small subcontractors rely on lightning-fast response times. Our single-state and regional crawlers scan continuously, mapping NIGP classifications directly to local school boards, municipal facility maintenance feeds, and roadway projects.
                    </p>
                    <ul className="space-y-3">
                      <li className="flex items-center gap-2.5 text-xs text-slate-600">
                        <CheckCircle2 className="w-4 h-4 text-indigo-600 shrink-0" />
                        <span><strong>Express Sourcing Matcher:</strong> Filters deals based on specific NIGP codes instantly.</span>
                      </li>
                      <li className="flex items-center gap-2.5 text-xs text-slate-600">
                        <CheckCircle2 className="w-4 h-4 text-indigo-600 shrink-0" />
                        <span><strong>Low-Overhead Drafting:</strong> Prepares executive summaries in minutes, keeping response teams lean.</span>
                      </li>
                    </ul>
                    <button onClick={() => setIsDemoModalOpen(true)} className="px-4 py-2.5 text-xs font-bold text-white bg-indigo-600 rounded-lg shadow hover:bg-indigo-700 cursor-pointer">
                      Explore Subcontractor Solutions
                    </button>
                  </div>
                  <div className="lg:col-span-5 bg-slate-50 p-6 rounded-2xl border border-slate-150 space-y-4">
                    <span className="text-[9px] font-black tracking-wider text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded uppercase">SUBCONTRACTOR USE CASE</span>
                    <blockquote className="text-xs text-slate-600 italic">
                      "As a small HVAC provider, we had no dedicated bidding team. RFXpress matched us to a $140K state building chiller replacement RFP and drafted the response in 1.5 hours. We won and scaled our business."
                    </blockquote>
                    <p className="text-[10px] font-bold text-slate-900">- Tyler Cross, Cross Facilities Systems</p>
                  </div>
                </motion.div>
              )}

              {activeSolutionTab === 'prime' && (
                <motion.div 
                  key="prime"
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -15 }}
                  className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center"
                >
                  <div className="lg:col-span-7 space-y-5 text-left">
                    <h3 className="text-xl font-extrabold text-slate-900">Enterprise Scale & Absolute Bid Compliance</h3>
                    <p className="text-xs text-slate-500 leading-relaxed">
                      Prime contractors handle large multi-state projects with absolute compliance requirements. RFXpress integrates with corporate document repositories to manage past performance references, track multiple concurrent deadlines, and coordinate joint-venture compliance targets.
                    </p>
                    <ul className="space-y-3">
                      <li className="flex items-center gap-2.5 text-xs text-slate-600">
                        <CheckCircle2 className="w-4 h-4 text-indigo-600 shrink-0" />
                        <span><strong>Corporate past performance locker:</strong> Auto-indexes files and parses references to meet RFP guidelines.</span>
                      </li>
                      <li className="flex items-center gap-2.5 text-xs text-slate-600">
                        <CheckCircle2 className="w-4 h-4 text-indigo-600 shrink-0" />
                        <span><strong>Multi-User Seats & Google Workspace:</strong> Direct document collaboration across real Google Docs.</span>
                      </li>
                    </ul>
                    <button onClick={() => setIsDemoModalOpen(true)} className="px-4 py-2.5 text-xs font-bold text-white bg-indigo-600 rounded-lg shadow hover:bg-indigo-700 cursor-pointer">
                      Explore Prime Sourcing Solutions
                    </button>
                  </div>
                  <div className="lg:col-span-5 bg-slate-50 p-6 rounded-2xl border border-slate-150 space-y-4">
                    <span className="text-[9px] font-black tracking-wider text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded uppercase">PRIME USE CASE</span>
                    <blockquote className="text-xs text-slate-600 italic">
                      "Managing bids across 15 states was a administrative nightmare. RFXpress automated our entire scanning process and compiled pre-audit responses, doubling our win rate on state transit projects."
                    </blockquote>
                    <p className="text-[10px] font-bold text-slate-900">- Sarah Jenkins, AeroGov Consulting</p>
                  </div>
                </motion.div>
              )}

              {activeSolutionTab === 'mwbe' && (
                <motion.div 
                  key="mwbe"
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -15 }}
                  className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center"
                >
                  <div className="lg:col-span-7 space-y-5 text-left">
                    <h3 className="text-xl font-extrabold text-slate-900">Maximize Minority-Owned Preference Set-Asides</h3>
                    <p className="text-xs text-slate-500 leading-relaxed">
                      Sovereign states and municipal agencies allocate billions to certified Minority, Women, and Veteran Owned Businesses (MWBE). RFXpress incorporates specialized matching algorithms that target set-aside contracts and optimize your local bidding credits.
                    </p>
                    <ul className="space-y-3">
                      <li className="flex items-center gap-2.5 text-xs text-slate-600">
                        <CheckCircle2 className="w-4 h-4 text-indigo-600 shrink-0" />
                        <span><strong>State Preference Multipliers:</strong> Calculates evaluation credits (e.g. CA SB/DVBE, TX HUB).</span>
                      </li>
                      <li className="flex items-center gap-2.5 text-xs text-slate-600">
                        <CheckCircle2 className="w-4 h-4 text-indigo-600 shrink-0" />
                        <span><strong>Partner Sourcing Matcher:</strong> Suggests qualified MWBE sub-alliances to exceed goals.</span>
                      </li>
                    </ul>
                    <button onClick={() => setIsDemoModalOpen(true)} className="px-4 py-2.5 text-xs font-bold text-white bg-indigo-600 rounded-lg shadow hover:bg-indigo-700 cursor-pointer">
                      Explore Set-Aside Sourcing
                    </button>
                  </div>
                  <div className="lg:col-span-5 bg-slate-50 p-6 rounded-2xl border border-slate-150 space-y-4">
                    <span className="text-[9px] font-black tracking-wider text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded uppercase">MWBE CERTIFIED SUCCESS</span>
                    <blockquote className="text-xs text-slate-600 italic">
                      "As a minority-owned IT consultancy, identifying the ideal set-aside rules was crucial. RFXpress matched us to set-aside contracts, ensuring our certified status gave us the optimal advantage."
                    </blockquote>
                    <p className="text-[10px] font-bold text-slate-900">- Dev Chen, IntegraTech Systems</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

        </div>
      </section>

      {/* INTERACTIVE PLAYGROUND CONSOLE (The crowning feature) */}
      <section className="py-24 px-6 bg-[#0B0C10] text-white relative overflow-hidden" id="playground-section">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,rgba(99,102,241,0.15),transparent_40%)]" />
        
        <div className="max-w-6xl mx-auto space-y-12 relative z-10">
          <div className="text-center space-y-3">
            <span className="text-[10px] font-black tracking-widest text-indigo-400 bg-indigo-950 border border-indigo-900 px-3 py-1 rounded-full uppercase">LIVE INTERACTIVE TERMINAL</span>
            <h2 className="text-3xl font-black tracking-tight text-white font-sans">Run Aura Autonomous Agent in Real Time</h2>
            <p className="text-slate-400 text-xs max-w-lg mx-auto">
              Simulate how our AI Bidding Copilot scans government databases, checks local policies, and drafts high-fidelity proposal chapters instantly.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
            
            {/* Control Board */}
            <div className="lg:col-span-4 bg-slate-950 p-6 rounded-2xl border border-slate-800 flex flex-col justify-between space-y-6">
              <div className="space-y-4">
                <p className="text-[10px] font-bold text-indigo-400 uppercase tracking-widest">Select Simulation Template</p>
                <div className="space-y-2.5">
                  <button 
                    onClick={() => {
                      setSelectedSandboxPreset('california');
                      executeSandboxAgent('california');
                    }}
                    disabled={isAgentRunning}
                    className={cn(
                      "w-full p-3 rounded-xl border text-left text-xs transition-all flex items-start gap-3 cursor-pointer",
                      selectedSandboxPreset === 'california' && !isRunningCustom
                        ? "bg-indigo-950/40 border-indigo-500 text-white shadow" 
                        : "bg-slate-900/60 border-slate-800 text-slate-400 hover:border-slate-700"
                    )}
                  >
                    <span className="w-2.5 h-2.5 rounded-full bg-indigo-500 mt-1 shrink-0" />
                    <div>
                      <h4 className="font-bold">California Cloud RFP Scan</h4>
                      <p className="text-[9px] text-slate-400 mt-0.5">NIGP 208-11 | CDT 15% Set-Asides</p>
                    </div>
                  </button>

                  <button 
                    onClick={() => {
                      setSelectedSandboxPreset('texas');
                      executeSandboxAgent('texas');
                    }}
                    disabled={isAgentRunning}
                    className={cn(
                      "w-full p-3 rounded-xl border text-left text-xs transition-all flex items-start gap-3 cursor-pointer",
                      selectedSandboxPreset === 'texas' && !isRunningCustom
                        ? "bg-indigo-950/40 border-indigo-500 text-white shadow" 
                        : "bg-slate-900/60 border-slate-800 text-slate-400 hover:border-slate-700"
                    )}
                  >
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-500 mt-1 shrink-0" />
                    <div>
                      <h4 className="font-bold">Texas Facilities HVAC Draft</h4>
                      <p className="text-[9px] text-slate-400 mt-0.5">TX Section 12-B | HUB Matcher</p>
                    </div>
                  </button>

                  <button 
                    onClick={() => {
                      setSelectedSandboxPreset('florida');
                      executeSandboxAgent('florida');
                    }}
                    disabled={isAgentRunning}
                    className={cn(
                      "w-full p-3 rounded-xl border text-left text-xs transition-all flex items-start gap-3 cursor-pointer",
                      selectedSandboxPreset === 'florida' && !isRunningCustom
                        ? "bg-indigo-950/40 border-indigo-500 text-white shadow" 
                        : "bg-slate-900/60 border-slate-800 text-slate-400 hover:border-slate-700"
                    )}
                  >
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 mt-1 shrink-0" />
                    <div>
                      <h4 className="font-bold">Florida Cybersecurity Audit</h4>
                      <p className="text-[9px] text-slate-400 mt-0.5">Chapter 287 | MWBE Allocations</p>
                    </div>
                  </button>
                </div>
              </div>

              {/* Custom Input */}
              <div className="space-y-3 pt-4 border-t border-slate-900">
                <p className="text-[10px] font-bold text-indigo-400 uppercase tracking-widest">Or Run Custom Bidding Instructions</p>
                <div className="relative">
                  <textarea 
                    value={customPlaygroundQuery}
                    onChange={(e) => setCustomPlaygroundQuery(e.target.value)}
                    placeholder="E.g., Scan Ohio state solar project opportunities, audit compliance, draft summary..."
                    disabled={isAgentRunning}
                    className="w-full h-20 text-[11px] p-2.5 bg-slate-900 border border-slate-800 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-500 placeholder:text-slate-600 text-white resize-none"
                  />
                  <button 
                    onClick={executeCustomSandbox}
                    disabled={isAgentRunning || !customPlaygroundQuery.trim()}
                    className="absolute bottom-2 right-2 p-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-30 disabled:cursor-not-allowed rounded-lg text-white transition cursor-pointer"
                    title="Execute Aura Agent"
                  >
                    {isAgentRunning && isRunningCustom ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5 fill-current" />}
                  </button>
                </div>
              </div>
            </div>

            {/* Simulated Live Console and Output screen */}
            <div className="lg:col-span-8 bg-slate-950 rounded-2xl border border-slate-800 overflow-hidden flex flex-col justify-between min-h-[420px]">
              {/* Window Bar */}
              <div className="bg-slate-900 px-4 py-3 border-b border-slate-800 flex justify-between items-center">
                <div className="flex items-center gap-2 font-mono text-[10px] text-slate-400">
                  <Terminal className="w-3.5 h-3.5 text-indigo-500" />
                  <span>Aura@rfxpress-agents:~</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={cn("w-2.5 h-2.5 rounded-full", isAgentRunning ? "bg-amber-500 animate-pulse" : "bg-emerald-500")} />
                  <span className="font-mono text-[9px] text-slate-400 uppercase">
                    {isAgentRunning ? "Running Agent Script..." : "Terminal Ready"}
                  </span>
                </div>
              </div>

              {/* Execution Logs */}
              <div className="flex-1 p-5 font-mono text-[10px] space-y-2.5 overflow-y-auto text-left border-b border-slate-900 bg-slate-950 max-h-48">
                {agentLogs.map((log, idx) => (
                  <div key={idx} className="flex gap-2">
                    <span className="text-slate-600 select-none">{idx + 1}.</span>
                    <span className={cn(
                      (log || '').includes("[Aura Scraper]") && "text-sky-400",
                      (log || '').includes("[Aura Locker]") && "text-purple-400",
                      (log || '').includes("[Aura Auditor]") && "text-amber-400",
                      (log || '').includes("[Gemini LLM]") && "text-indigo-400"
                    )}>
                      {log}
                    </span>
                  </div>
                ))}
                {isAgentRunning && agentLogs.length === 0 && (
                  <p className="text-slate-600 animate-pulse">// Scrapers deploying. Pinging state APIs...</p>
                )}
                {agentLogs.length === 0 && !isAgentRunning && (
                  <p className="text-slate-500">// Select a preset or type a prompt, then execute Aura.</p>
                )}
              </div>

              {/* Dynamic Draft Content Output Area */}
              <div className="flex-1 p-5 bg-[#0e1017] border-t border-slate-900/60 overflow-y-auto h-72 text-left font-sans prose prose-invert prose-xs">
                {streamedText ? (
                  <div className="text-slate-300 font-normal space-y-4">
                    {/* Parse a bit of markdown headers manually for layout cleanliness */}
                    {streamedText.split('\n').map((line, idx) => {
                      if (line.startsWith('### ')) {
                        return <h3 key={idx} className="text-sm font-extrabold text-white mt-4 border-b border-slate-800 pb-1 uppercase tracking-wide">{line.replace('### ', '')}</h3>;
                      }
                      if (line.startsWith('**') && line.endsWith('**')) {
                        return <p key={idx} className="text-xs text-indigo-400 font-bold mt-2">{line.replace(/\*\*/g, '')}</p>;
                      }
                      if (line.startsWith('#### ')) {
                        return <h4 key={idx} className="text-xs font-bold text-indigo-300 mt-3">{line.replace('#### ', '')}</h4>;
                      }
                      if (line.startsWith('* ')) {
                        return <li key={idx} className="text-[11px] list-disc ml-4 text-slate-300">{line.replace('* ', '')}</li>;
                      }
                      return <p key={idx} className="text-[11px] leading-relaxed mt-1">{line}</p>;
                    })}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full space-y-2.5 text-slate-500 text-center py-10">
                    <Database className="w-8 h-8 text-slate-700 animate-pulse" />
                    <p className="text-xs italic font-normal">Aura proposal generation stream will display here...</p>
                  </div>
                )}
              </div>

              {/* Actions row */}
              <div className="bg-slate-900 px-4 py-3 flex justify-between items-center text-[10px] text-slate-400 border-t border-slate-800">
                <span className="flex items-center gap-1.5 font-normal">
                  <Cloud className="w-3.5 h-3.5 text-indigo-400" />
                  Compiled ready for Google Workspace Docs
                </span>
                <button 
                  onClick={() => {
                    if (isAgentRunning) return;
                    handleGoogleLogin();
                  }}
                  className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-md transition cursor-pointer flex items-center gap-1 shadow-sm"
                >
                  <ArrowUpRight className="w-3.5 h-3.5" />
                  Ingest Draft in Portal
                </button>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* COMPREHENSIVE FEATURES DEEP-DIVE (Bento Grid Style) */}
      <section className="py-24 px-6 bg-white" id="bento-features">
        <div className="max-w-6xl mx-auto space-y-16">
          <div className="text-center space-y-3">
            <span className="text-[10px] font-black tracking-widest text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-full uppercase">THE BENTO CAPABILITIES ENGINE</span>
            <h2 className="text-3xl font-black text-slate-900 tracking-tight">The Complete Sourcing & Proposal Suite</h2>
            <p className="text-slate-500 text-xs max-w-lg mx-auto">
              Every specialized crawler, database collection, and security check is engineered into a unified responsive dashboard.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            
            {/* Scraper Card */}
            <Card className="p-6 space-y-4 border-slate-200 hover:border-indigo-300 transition-all text-left bg-[#FDFDFD]">
              <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl w-fit">
                <Database className="w-5 h-5" />
              </div>
              <h3 className="font-extrabold text-slate-900 text-sm">50-State Multi-Portal Scraper</h3>
              <p className="text-xs text-slate-500 leading-relaxed">
                Connects directly to major public state databases. Active scrapers index bidding categories, description sheets, threshold values, and NIGP listings daily.
              </p>
            </Card>

            {/* Writer Card */}
            <Card className="p-6 space-y-4 border-slate-200 hover:border-indigo-300 transition-all text-left bg-[#FDFDFD]">
              <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl w-fit">
                <Cpu className="w-5 h-5" />
              </div>
              <h3 className="font-extrabold text-slate-900 text-sm">Aura Proposal Draftsman</h3>
              <p className="text-xs text-slate-500 leading-relaxed">
                Automatically formulates high-scoring proposal outlines, staffing matrices, technical approaches, and standard summaries customized to state agency rules.
              </p>
            </Card>

            {/* Locker Card */}
            <Card className="p-6 space-y-4 border-slate-200 hover:border-indigo-300 transition-all text-left bg-[#FDFDFD]">
              <div className="p-3 bg-purple-50 text-purple-600 rounded-xl w-fit">
                <Layers className="w-5 h-5" />
              </div>
              <h3 className="font-extrabold text-slate-900 text-sm">Corporate Past Performance Locker</h3>
              <p className="text-xs text-slate-500 leading-relaxed">
                Safely aggregates your previous proposal drafts, credentials, insurance certifications, and compliance letters to ground Gemini response accuracy.
              </p>
            </Card>

            {/* Google Sync */}
            <Card className="p-6 space-y-4 border-slate-200 hover:border-indigo-300 transition-all text-left bg-[#FDFDFD]">
              <div className="p-3 bg-blue-50 text-blue-600 rounded-xl w-fit">
                <Cloud className="w-5 h-5" />
              </div>
              <h3 className="font-extrabold text-slate-900 text-sm">Google Workspace Integration</h3>
              <p className="text-xs text-slate-500 leading-relaxed">
                Securely connects to your enterprise Google Drive and Docs. Aura drafts compile directly into collaborative online folders for rapid review.
              </p>
            </Card>

            {/* Calendar & Deadlines */}
            <Card className="p-6 space-y-4 border-slate-200 hover:border-indigo-300 transition-all text-left bg-[#FDFDFD]">
              <div className="p-3 bg-amber-50 text-amber-600 rounded-xl w-fit">
                <Calendar className="w-5 h-5" />
              </div>
              <h3 className="font-extrabold text-slate-900 text-sm">Dynamic Bid Deadline Scheduler</h3>
              <p className="text-xs text-slate-500 leading-relaxed">
                An interactive calendar interface tracking agency submission dates. Syncs schedules with your Google Calendar, deploying automated email alerts.
              </p>
            </Card>

            {/* Compliance Auditer */}
            <Card className="p-6 space-y-4 border-slate-200 hover:border-indigo-300 transition-all text-left bg-[#FDFDFD]">
              <div className="p-3 bg-rose-50 text-rose-600 rounded-xl w-fit">
                <Shield className="w-5 h-5" />
              </div>
              <h3 className="font-extrabold text-slate-900 text-sm">MWBE Preference Verifier</h3>
              <p className="text-xs text-slate-500 leading-relaxed">
                Evaluates state preference models, local tax credit scaling rules, HUB targets, and mandatory contractor bonding levels to ensure perfect compliance.
              </p>
            </Card>

          </div>
        </div>
      </section>

      {/* POLISHED ROI ESTIMATOR */}
      <section className="py-24 px-6 bg-slate-50 border-y border-slate-200" id="roi-calculator">
        <div className="max-w-4xl mx-auto space-y-12">
          <div className="text-center space-y-3">
            <span className="text-[10px] font-black tracking-widest text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-full uppercase font-sans">SLA OVERHEAD CALCULATOR</span>
            <h2 className="text-3xl font-black text-slate-900 tracking-tight">Quantify Your Sourcing Cost Savings</h2>
            <p className="text-slate-500 text-xs max-w-md mx-auto">
              Sourcing and writing bid responses manually consumes days. Slide the selector to calculate the true financial value.
            </p>
          </div>

          <Card className="p-8 border-indigo-100 bg-indigo-50/20 grid grid-cols-1 md:grid-cols-2 gap-8 items-center rounded-2xl">
            <div className="space-y-6 text-left">
              <div className="space-y-2">
                <div className="flex justify-between items-center text-xs font-bold text-slate-600">
                  <span>RFX Responses Evaluated Monthly:</span>
                  <span className="text-indigo-600 font-extrabold bg-indigo-50 px-2.5 py-1 rounded font-mono">{roiVolume} Responses</span>
                </div>
                <input 
                  type="range" 
                  min="2" 
                  max="40" 
                  value={roiVolume} 
                  onChange={(e) => setRoiVolume(parseInt(e.target.value))}
                  className="w-full accent-indigo-600 cursor-pointer h-1.5 bg-slate-200 rounded-lg appearance-none"
                />
              </div>

              <div className="space-y-3.5 text-xs text-slate-650 pt-2 border-t border-dashed border-indigo-150">
                <p className="flex justify-between font-normal">
                  <span>Manual Labor per Proposal:</span>
                  <span className="font-medium text-slate-900 font-mono">30 hours</span>
                </p>
                <p className="flex justify-between font-normal">
                  <span>SaaS AI Bidding Duration:</span>
                  <span className="font-bold text-indigo-600 font-mono">2 hours (93% reduction)</span>
                </p>
                <p className="flex justify-between font-normal">
                  <span>Assumed Professional Labor Rate:</span>
                  <span className="font-medium text-slate-900 font-mono">$75 / hour</span>
                </p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl border border-indigo-100 text-center space-y-4 shadow-sm">
              <span className="text-[9px] font-black tracking-wider text-indigo-600 bg-indigo-50 px-3 py-1 rounded-full uppercase">ESTIMATED REALLOCATED SAVINGS</span>
              <div className="space-y-1">
                <h3 className="text-4xl font-black text-emerald-600 font-mono">${estimatedCostSaved.toLocaleString()}</h3>
                <p className="text-xs text-slate-400 font-bold">{estimatedHoursSaved} Executive Hours Reclaimed / Mo</p>
              </div>
              <p className="text-[11px] text-slate-500 leading-relaxed font-normal">
                Easily offsets the <span className="font-bold text-slate-900">$119 / mo</span> Professional Tier subscription value.
              </p>
            </div>
          </Card>
        </div>
      </section>

      {/* CONTRACTOR TESTIMONIALS */}
      <section className="py-24 px-6 bg-white" id="testimonials">
        <div className="max-w-6xl mx-auto space-y-16">
          <div className="text-center space-y-3">
            <span className="text-[10px] font-black tracking-widest text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-full uppercase">CUSTOMER ENDORSEMENTS</span>
            <h2 className="text-3xl font-black text-slate-900 tracking-tight">Government Contractors Winning with Aura</h2>
            <p className="text-slate-500 text-xs max-w-md mx-auto">
              Read true accounts of subcontractors and prime vendors securing state tenders via the unified platform.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((test, idx) => (
              <Card key={idx} className="p-6 border-slate-150 bg-slate-50/50 space-y-5 flex flex-col justify-between rounded-xl">
                <div className="space-y-3 text-left">
                  <div className="flex justify-between items-center">
                    <div className="flex gap-0.5 text-amber-500">
                      {[...Array(test.rating)].map((_, i) => (
                        <Star key={i} className="w-3.5 h-3.5 fill-amber-500 text-amber-500" />
                      ))}
                    </div>
                    <span className="text-[9px] font-bold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded font-mono uppercase tracking-wider">{test.metric}</span>
                  </div>
                  <p className="text-xs text-slate-600 italic leading-relaxed font-normal">
                    "{test.quote}"
                  </p>
                </div>

                <div className="flex items-center gap-3 pt-4 border-t border-slate-200">
                  <img src={test.avatar} alt={test.author} className="w-9 h-9 rounded-full object-cover shrink-0" />
                  <div className="text-left">
                    <h5 className="text-xs font-bold text-slate-900">{test.author}</h5>
                    <p className="text-[10px] text-slate-400 font-medium">{test.role}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* COMPREHENSIVE PRICING MODULE WITH PAYPAL CHECKOUT REGRESSION SAFE */}
      <section className="py-24 px-6 bg-slate-50 border-t border-slate-200" id="pricing">
        <div className="max-w-6xl mx-auto space-y-16 text-center">
          
          <div className="space-y-4">
            <span className="text-[10px] font-black tracking-widest text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-full uppercase">SECURE CORPORATE TIERS</span>
            <h2 className="text-3xl font-black text-slate-900 tracking-tight">Flexible Membership Plans</h2>
            <p className="text-slate-500 text-xs max-w-md mx-auto">
              Select your procurement bandwidth. Secure PayPal checkout unlocks professional multi-state crawlers and document automation instantly.
            </p>

            {/* BILLING TOGGLE */}
            <div className="inline-flex items-center gap-1.5 p-1 bg-white border border-slate-200 rounded-full shadow-inner mt-4">
              <button 
                onClick={() => setBillingCycle('monthly')}
                className={cn(
                  "px-4 py-1.5 text-xs font-bold rounded-full transition-all cursor-pointer",
                  billingCycle === 'monthly' ? 'bg-indigo-600 text-white shadow' : 'text-slate-500 hover:text-slate-900'
                )}
              >
                Monthly Billing
              </button>
              <button 
                onClick={() => setBillingCycle('annual')}
                className={cn(
                  "px-4 py-1.5 text-xs font-bold rounded-full transition-all cursor-pointer",
                  billingCycle === 'annual' ? 'bg-indigo-600 text-white shadow' : 'text-slate-500 hover:text-slate-900'
                )}
              >
                Annual Save 20%
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {pricingPlans.map((plan) => {
              const currentPrice = billingCycle === 'monthly' ? plan.monthlyPrice : plan.annualPrice;
              return (
                <div 
                  key={plan.name}
                  className={cn(
                    "p-6 bg-white border rounded-2xl shadow-sm flex flex-col justify-between text-left transition-all relative",
                    plan.popular ? 'ring-2 ring-indigo-600/30 shadow-md border-indigo-200' : 'border-slate-200 hover:border-slate-300'
                  )}
                >
                  {plan.popular && (
                    <span className="absolute -top-3 left-6 px-3 py-0.5 text-[9px] font-bold text-white bg-indigo-600 rounded-full tracking-wider uppercase shadow-sm">
                      Best Sourcing Value
                    </span>
                  )}
                  
                  <div className="space-y-6">
                    <div>
                      <span className="text-[10px] font-black tracking-widest text-indigo-600 bg-indigo-50 px-2.5 py-0.5 rounded-md uppercase font-mono">
                        {plan.badge}
                      </span>
                      <h3 className="text-lg font-black text-slate-950 mt-2 font-sans">{plan.name}</h3>
                      <p className="text-xs text-slate-500 mt-1 leading-snug font-normal">{plan.tagline}</p>
                    </div>

                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-black text-slate-950 font-mono">${currentPrice}</span>
                      <span className="text-xs text-slate-400 font-bold font-sans">/ month</span>
                      {billingCycle === 'annual' && (
                        <span className="text-[9px] text-emerald-600 font-black ml-2 bg-emerald-50 px-1.5 py-0.5 rounded-sm">Billed annually</span>
                      )}
                    </div>

                    <div className="h-px bg-slate-100" />

                    <ul className="space-y-3.5">
                      {plan.features.map((feat) => (
                        <li key={feat} className="flex gap-2 text-xs text-slate-600 items-start font-normal">
                          <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                          <span>{feat}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="pt-8">
                    <button 
                      id={`btn-purchase-${plan.tierId}`}
                      onClick={() => handleOpenCheckout(plan)}
                      className={cn(
                        "w-full py-3.5 text-xs font-bold rounded-xl transition-all cursor-pointer flex items-center justify-center gap-1.5 shadow-sm",
                        plan.popular ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-indigo-150' : 'bg-slate-50 text-slate-800 border border-slate-200 hover:bg-slate-100'
                      )}
                    >
                      Subscribe with PayPal
                      <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ACCORDION FAQS */}
      <section className="py-24 px-6 bg-white border-t border-slate-200" id="faqs">
        <div className="max-w-3xl mx-auto space-y-12">
          <div className="text-center space-y-3">
            <span className="text-[10px] font-black tracking-widest text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-full uppercase">PROCUREMENT ANSWERS</span>
            <h2 className="text-3xl font-black text-slate-900 tracking-tight font-sans">Frequently Asked Questions</h2>
            <p className="text-slate-500 text-xs max-w-md mx-auto">
              Learn how we scrape 50 states, maintain perfect compliance, and clear invoicing through PayPal securely.
            </p>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <Card 
                key={idx} 
                className="p-5 border-slate-150 bg-[#FAFAFA] hover:border-indigo-200 transition-all cursor-pointer"
                onClick={() => toggleFaq(idx)}
              >
                <div className="flex justify-between items-center">
                  <h4 className="text-xs font-extrabold text-slate-900 flex items-center gap-2">
                    <HelpCircle className="w-4 h-4 text-indigo-500" />
                    {faq.question}
                  </h4>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${faqOpen[idx] ? 'rotate-180' : ''}`} />
                </div>
                
                <AnimatePresence>
                  {faqOpen[idx] && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <p className="text-xs text-slate-500 mt-3 pl-6 leading-relaxed border-l-2 border-indigo-100 font-normal">
                        {faq.answer}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* COMPREHENSIVE FOOTER */}
      <footer className="bg-[#0B0C10] text-slate-400 py-16 px-6 border-t border-slate-900 text-xs">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-12 text-left">
          <div className="space-y-4">
            <div className="flex items-center gap-2.5 text-white">
              <Zap className="text-indigo-400 w-5 h-5 animate-pulse" />
              <span className="font-extrabold text-base tracking-tight text-white">RFXpress SaaS</span>
            </div>
            <p className="text-[11px] text-slate-500 leading-relaxed font-normal">
              Autonomous bid proposal engineering. Monitor, parse, evaluate, and compile state RFX solicitations daily using ground-breaking AI compliance agents.
            </p>
            <div className="pt-2">
              <span className="px-2 py-1 text-[9px] font-mono text-emerald-400 bg-emerald-950/50 border border-emerald-900 rounded">
                ● SECURITY STANDARD INGEST ACTIVE
              </span>
            </div>
          </div>

          <div className="space-y-3">
            <h5 className="font-bold text-white text-[11px] uppercase tracking-widest text-indigo-400">Products & Features</h5>
            <ul className="space-y-2 text-[11px] text-slate-500 font-normal">
              <li><a href="#playground-section" className="hover:text-white transition-colors">Aura Autonomous Agent</a></li>
              <li><a href="#bento-features" className="hover:text-white transition-colors">50-State Portal Crawlers</a></li>
              <li><a href="#bento-features" className="hover:text-white transition-colors">Past Performance Locker</a></li>
              <li><a href="#bento-features" className="hover:text-white transition-colors">Google Docs Workspace Compile</a></li>
              <li><a href="#roi-calculator" className="hover:text-white transition-colors">ROI Estimator</a></li>
            </ul>
          </div>

          <div className="space-y-3">
            <h5 className="font-bold text-white text-[11px] uppercase tracking-widest text-indigo-400">Supported Portals</h5>
            <ul className="space-y-2 text-[11px] text-slate-500 font-normal">
              <li>California (Cal eProcure Scraper)</li>
              <li>Texas State Comptroller Portal</li>
              <li>New York State Reporter Feed</li>
              <li>Florida Vendor Registry scraper</li>
              <li>Federal Sourcing Feeds (Upcoming)</li>
            </ul>
          </div>

          <div className="space-y-4">
            <h5 className="font-bold text-white text-[11px] uppercase tracking-widest text-indigo-400">Escrow Clearing</h5>
            <p className="text-[11px] text-slate-500 leading-normal font-normal">
              Secure subscription processing operated via authorized gateway of PayPal, Inc. Detailed merchant invoicing generated on plan confirmation.
            </p>
            <div className="flex gap-2">
              <span className="font-mono text-[9px] text-indigo-400 bg-slate-900 px-1.5 py-0.5 rounded border border-slate-800">PAYPAL_VERIFIED</span>
              <span className="font-mono text-[9px] text-emerald-400 bg-slate-900 px-1.5 py-0.5 rounded border border-slate-800">SOC_2_COMPLIANT</span>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto pt-8 mt-12 border-t border-slate-900 text-center text-[10px] text-slate-600 font-mono">
          © 2026 RFXpress SaaS Technologies Inc. Cloned from procure.ai design principles. All state-local rights reserved.
        </div>
      </footer>

      {/* BOOK DEMO MODAL DIALOG */}
      <AnimatePresence>
        {isDemoModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/65 backdrop-blur-xs">
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white border border-slate-200 rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden flex flex-col text-left"
            >
              {/* Modal Header */}
              <div className="bg-[#0B0C10] px-6 py-4 flex justify-between items-center text-white">
                <div className="flex items-center gap-2">
                  <Zap className="text-indigo-400 w-4 h-4 animate-pulse" />
                  <span className="font-extrabold text-sm tracking-tight">Schedule Aura AI Product Demo</span>
                </div>
                <button 
                  onClick={closeDemoModal}
                  className="p-1 text-slate-300 hover:text-white transition-colors cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Step Render */}
              <div className="p-6">
                {demoStep === 'form' ? (
                  <form onSubmit={handleDemoSubmit} className="space-y-4">
                    <p className="text-xs text-slate-500 font-normal leading-relaxed">
                      Connect with our public sector experts. We'll show you how Aura indexes matching solicitations for your specific NAICS/NIGP classifications.
                    </p>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold text-slate-600 uppercase">Your Name *</label>
                        <input 
                          type="text"
                          required
                          value={demoFormData.name}
                          onChange={(e) => setDemoFormData({...demoFormData, name: e.target.value})}
                          placeholder="Jane Doe"
                          className="w-full text-xs p-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-indigo-500 outline-none transition-all"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold text-slate-600 uppercase">Work Email *</label>
                        <input 
                          type="email"
                          required
                          value={demoFormData.email}
                          onChange={(e) => setDemoFormData({...demoFormData, email: e.target.value})}
                          placeholder="jane@company.com"
                          className="w-full text-xs p-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-indigo-500 outline-none transition-all"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold text-slate-600 uppercase">Organization Name</label>
                        <input 
                          type="text"
                          value={demoFormData.company}
                          onChange={(e) => setDemoFormData({...demoFormData, company: e.target.value})}
                          placeholder="GovTech Inc."
                          className="w-full text-xs p-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-indigo-500 outline-none transition-all"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold text-slate-600 uppercase">Annual Public Bid Volume</label>
                        <select 
                          value={demoFormData.revenue}
                          onChange={(e) => setDemoFormData({...demoFormData, revenue: e.target.value})}
                          className="w-full text-xs p-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-indigo-500 outline-none transition-all"
                        >
                          <option>&lt; $1M / year</option>
                          <option>$1M - $5M / year</option>
                          <option>$5M - $20M / year</option>
                          <option>&gt; $20M / year</option>
                        </select>
                      </div>
                    </div>

                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-slate-600 uppercase">Primary Commodity Classifications</label>
                      <select 
                        value={demoFormData.category}
                        onChange={(e) => setDemoFormData({...demoFormData, category: e.target.value})}
                        className="w-full text-xs p-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-indigo-500 outline-none transition-all"
                      >
                        <option>Information Technology (NAICS 541511)</option>
                        <option>HVAC & Building Maintenance (NIGP 918-28)</option>
                        <option>Engineering & Infrastructure (NAICS 236220)</option>
                        <option>Professional Consulting (NIGP 918-29)</option>
                      </select>
                    </div>

                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-slate-600 uppercase">State Portals of High Interest</label>
                      <select 
                        value={demoFormData.state}
                        onChange={(e) => setDemoFormData({...demoFormData, state: e.target.value})}
                        className="w-full text-xs p-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-indigo-500 outline-none transition-all"
                      >
                        <option>California (Cal eProcure)</option>
                        <option>Texas (Comptroller Registry)</option>
                        <option>Florida (Vendor Registry)</option>
                        <option>New York State Reporter</option>
                      </select>
                    </div>

                    <div className="pt-3 flex gap-2">
                      <button 
                        type="button"
                        onClick={closeDemoModal}
                        className="flex-1 py-3 text-xs font-bold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg cursor-pointer"
                      >
                        Cancel
                      </button>
                      <button 
                        type="submit"
                        className="flex-1 py-3 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg cursor-pointer shadow-md text-center"
                      >
                        Schedule Strategy Session
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="space-y-5 text-center py-6">
                    <div className="w-12 h-12 bg-emerald-50 text-emerald-600 border border-emerald-100 rounded-full flex items-center justify-center mx-auto">
                      <Check className="w-6 h-6" />
                    </div>

                    <div className="space-y-1">
                      <h4 className="text-sm font-bold text-slate-900">Live Demo Invitation Dispatched!</h4>
                      <p className="text-[11px] text-slate-500 font-normal">Strategy Session Link: <span className="font-mono text-indigo-600 font-bold">meet.google.com/rfx-demo-session</span></p>
                    </div>

                    <div className="p-4 bg-slate-50 border border-slate-150 rounded-xl text-left space-y-2 text-[11px] text-slate-700 font-normal">
                      <p className="flex justify-between">
                        <span>Assigned Specialist:</span>
                        <strong className="text-slate-900">Sarah Vance, Sourcing Director</strong>
                      </p>
                      <p className="flex justify-between">
                        <span>Target Profile Scope:</span>
                        <span className="text-slate-600 truncate max-w-[180px]">{demoFormData.category}</span>
                      </p>
                      <p className="flex justify-between">
                        <span>Date & Time Scheduled:</span>
                        <span className="text-slate-600">Tomorrow, 10:00 AM PST</span>
                      </p>
                    </div>

                    <button 
                      onClick={closeDemoModal}
                      className="w-full py-3 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg cursor-pointer shadow-sm"
                    >
                      Close & Return to Tour
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* GOOGLE OAUTH CONFIGURATION MODAL (Environment Context-Aware OAuth Redirects) */}
      <AnimatePresence>
        {oauthErrorModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-xs">
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white border border-slate-200 rounded-2xl shadow-2xl max-w-xl w-full overflow-hidden flex flex-col text-left max-h-[90vh]"
            >
              {/* Header */}
              <div className="bg-slate-900 px-6 py-4 flex justify-between items-center text-white border-b border-slate-800">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-amber-500/20 text-amber-400 flex items-center justify-center">
                    <AlertTriangle className="w-4 h-4" />
                  </div>
                  <div>
                    <span className="font-bold text-sm tracking-tight text-white block">OAuth 2.0 Environment Configuration</span>
                    <span className="text-[11px] text-slate-400">Context-Aware Redirects for Codespaces, AI Studio &amp; Cloud Run</span>
                  </div>
                </div>
                <button 
                  onClick={() => setOauthErrorModalOpen(false)}
                  className="p-1 text-slate-400 hover:text-white transition-colors cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-4 overflow-y-auto">
                {/* Active Environment Banner */}
                <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-xl flex items-center justify-between text-xs">
                  <div>
                    <span className="font-semibold text-indigo-900 block">Detected Runtime Environment:</span>
                    <span className="font-mono text-indigo-700 font-bold text-[11px]">
                      {currentAuthInfo.environmentType === 'codespaces' ? '⚡ GitHub Codespaces' :
                       currentAuthInfo.environmentType === 'aistudio' ? '✨ AI Studio Cloud Run' :
                       currentAuthInfo.environmentType === 'production' ? '🚀 Cloud Run Production (rfxpress.ai.studio)' :
                       currentAuthInfo.environmentType === 'localhost' ? '💻 Localhost Development' : '🌐 Custom Server'}
                    </span>
                  </div>
                  <span className="px-2 py-0.5 bg-indigo-200 text-indigo-800 rounded text-[10px] font-mono font-bold">
                    {currentAuthInfo.host}
                  </span>
                </div>

                <div className="text-xs text-slate-600 leading-relaxed">
                  When developing in <strong>GitHub Codespaces</strong> or <strong>AI Studio</strong> without Firebase Hosting, the application's built-in Express proxy serves <code className="bg-slate-100 font-mono px-1 rounded">/__/auth/*</code> directly under your active domain. Add these Authorized Redirect URIs in your Google Cloud Console for project <code className="bg-amber-100 font-mono px-1 rounded font-bold">apexcite-3aece</code>:
                </div>

                {/* URI 1: Current Environment Active Redirect URI */}
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <label className="text-[11px] font-bold text-slate-700 uppercase">
                      1. Current Environment Redirect URI ({currentAuthInfo.environmentType})
                    </label>
                    <span className="text-[10px] text-emerald-600 font-medium">Active in this session</span>
                  </div>
                  <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg p-2">
                    <code className="text-[11px] font-mono text-slate-800 break-all flex-1 select-all">
                      {currentAuthInfo.redirectUri}
                    </code>
                    <button 
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(currentAuthInfo.redirectUri);
                        setCopiedRedirectUri(true);
                        setTimeout(() => setCopiedRedirectUri(false), 3000);
                      }}
                      className="px-2.5 py-1 bg-white border border-slate-300 rounded text-xs font-semibold text-slate-700 hover:bg-slate-100 flex items-center gap-1 cursor-pointer transition-colors shrink-0"
                    >
                      {copiedRedirectUri ? (
                        <>
                          <Check className="w-3.5 h-3.5 text-emerald-600" />
                          <span className="text-emerald-600">Copied</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-3.5 h-3.5 text-slate-600" />
                          <span>Copy</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* URI 2: Production Cloud Run (rfxpress.ai.studio) */}
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <label className="text-[11px] font-bold text-slate-700 uppercase">
                      2. Cloud Run Production Redirect URI
                    </label>
                    <span className="text-[10px] text-slate-500 font-mono">rfxpress.ai.studio</span>
                  </div>
                  <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg p-2">
                    <code className="text-[11px] font-mono text-slate-800 break-all flex-1 select-all">
                      https://rfxpress.ai.studio/__/auth/handler
                    </code>
                    <button 
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText('https://rfxpress.ai.studio/__/auth/handler');
                      }}
                      className="px-2.5 py-1 bg-white border border-slate-300 rounded text-xs font-semibold text-slate-700 hover:bg-slate-100 flex items-center gap-1 cursor-pointer transition-colors shrink-0"
                    >
                      <Copy className="w-3.5 h-3.5 text-slate-600" />
                      <span>Copy</span>
                    </button>
                  </div>
                </div>

                {/* URI 3: Firebase Default Upstream */}
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <label className="text-[11px] font-bold text-slate-700 uppercase">
                      3. Firebase Auth Fallback URI
                    </label>
                    <span className="text-[10px] text-slate-500 font-mono">apexcite-3aece.firebaseapp.com</span>
                  </div>
                  <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg p-2">
                    <code className="text-[11px] font-mono text-slate-800 break-all flex-1 select-all">
                      https://apexcite-3aece.firebaseapp.com/__/auth/handler
                    </code>
                    <button 
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText('https://apexcite-3aece.firebaseapp.com/__/auth/handler');
                      }}
                      className="px-2.5 py-1 bg-white border border-slate-300 rounded text-xs font-semibold text-slate-700 hover:bg-slate-100 flex items-center gap-1 cursor-pointer transition-colors shrink-0"
                    >
                      <Copy className="w-3.5 h-3.5 text-slate-600" />
                      <span>Copy</span>
                    </button>
                  </div>
                </div>

                {/* Setup Steps */}
                <div className="space-y-2 text-xs text-slate-600 border-t border-slate-100 pt-3">
                  <h4 className="font-bold text-slate-900">How to configure in Google Cloud Console:</h4>
                  <ol className="list-decimal list-inside space-y-1 pl-1 leading-relaxed text-[11px]">
                    <li>Open <strong>APIs &amp; Services &gt; Credentials</strong> in Google Cloud for project <code className="font-mono text-indigo-600">apexcite-3aece</code>.</li>
                    <li>Click your <strong>OAuth 2.0 Web Client ID</strong>.</li>
                    <li>Under <strong>Authorized JavaScript origins</strong>, add <code className="bg-slate-100 px-1 py-0.5 rounded font-mono text-[10px]">https://{currentAuthInfo.host}</code> and <code className="bg-slate-100 px-1 py-0.5 rounded font-mono text-[10px]">https://rfxpress.ai.studio</code>.</li>
                    <li>Under <strong>Authorized redirect URIs</strong>, add the redirect URIs copied above and click <strong>SAVE</strong>.</li>
                  </ol>
                </div>

                {/* Domain Mode Switcher */}
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
                  <span className="text-[11px] font-bold text-slate-700 uppercase block">Auth Domain Mode Override</span>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <button
                      type="button"
                      onClick={() => {
                        setCustomAuthDomain(null);
                        window.location.reload();
                      }}
                      className={`p-2 rounded-lg border text-left cursor-pointer transition-all ${
                        !currentAuthInfo.isCustomOverride
                          ? 'border-indigo-600 bg-indigo-50/50 text-indigo-900 font-semibold'
                          : 'border-slate-200 bg-white hover:bg-slate-100 text-slate-700'
                      }`}
                    >
                      <span className="block font-bold text-[11px]">Auto Context Domain</span>
                      <span className="text-[10px] text-slate-500 block truncate">{currentAuthInfo.host}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setCustomAuthDomain('apexcite-3aece.firebaseapp.com');
                        window.location.reload();
                      }}
                      className={`p-2 rounded-lg border text-left cursor-pointer transition-all ${
                        currentAuthInfo.isCustomOverride && currentAuthInfo.authDomain === 'apexcite-3aece.firebaseapp.com'
                          ? 'border-indigo-600 bg-indigo-50/50 text-indigo-900 font-semibold'
                          : 'border-slate-200 bg-white hover:bg-slate-100 text-slate-700'
                      }`}
                    >
                      <span className="block font-bold text-[11px]">Firebase Default</span>
                      <span className="text-[10px] text-slate-500 block truncate">apexcite-3aece.firebaseapp.com</span>
                    </button>
                  </div>
                </div>

                {/* Actions */}
                <div className="pt-2 flex flex-col sm:flex-row gap-3">
                  <a 
                    href="https://console.cloud.google.com/apis/credentials?project=apexcite-3aece"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 py-2.5 px-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-lg flex items-center justify-center gap-1.5 shadow-sm transition-colors text-center"
                  >
                    <span>Open Google Cloud Credentials</span>
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                  <button 
                    type="button"
                    onClick={() => {
                      setOauthErrorModalOpen(false);
                      handleGoogleLogin();
                    }}
                    className="py-2.5 px-4 bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold text-xs rounded-lg transition-colors cursor-pointer"
                  >
                    Retry Sign-In
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* PAYPAL SECURE CHECKOUT MODAL DIALOG (Carried over to protect Paypal subscription logic) */}
      <AnimatePresence>
        {selectedPlanForCheckout && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/65 backdrop-blur-xs">
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white border border-slate-200 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden flex flex-col text-left"
            >
              {/* PAYPAL HEADER ACCENT */}
              <div className="bg-[#003087] px-6 py-4 flex justify-between items-center text-white">
                <div className="flex items-center gap-1">
                  <span className="font-black italic text-lg tracking-tight text-white flex items-center">
                    Pay
                    <span className="text-[#0079C1]">Pal</span>
                  </span>
                  <span className="text-[10px] font-bold tracking-widest uppercase text-slate-300 border-l border-slate-500 pl-2.5 ml-2.5">
                    Secure Checkout
                  </span>
                </div>
                <button 
                  onClick={() => setSelectedPlanForCheckout(null)}
                  className="p-1 text-slate-300 hover:text-white transition-colors cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-5">
                {/* PLAN DETAIL BOX */}
                <div className="p-4 bg-slate-50 border border-slate-150 rounded-xl space-y-2.5">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-[10px] text-gray-400 font-bold uppercase">PLAN SELECTED</p>
                      <h4 className="font-extrabold text-[#0A0B10] text-sm mt-0.5">{selectedPlanForCheckout.name}</h4>
                    </div>
                    <span className="text-sm font-black text-indigo-600 bg-indigo-50 border border-indigo-100 px-2.5 py-0.5 rounded-md">
                      ${billingCycle === 'monthly' ? selectedPlanForCheckout.monthlyPrice : selectedPlanForCheckout.annualPrice}/mo
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-500 leading-normal font-normal">
                    {billingCycle === 'annual' ? 'Billed annually with 20% discount applied.' : 'Billed monthly. Cancel at any time in billing settings.'}
                  </p>
                </div>

                {checkoutStep === 'paypal_auth' && (
                  <div className="space-y-4">
                    <div className="space-y-1">
                      <p className="text-[11px] font-bold text-slate-600">PayPal Account Email</p>
                      <input 
                        id="paypal-checkout-email"
                        type="email"
                        placeholder="your-paypal-email@company.com"
                        value={paypalEmail}
                        onChange={(e) => setPaypalEmail(e.target.value)}
                        className="w-full text-xs p-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-[#003087] outline-none transition-all"
                      />
                    </div>

                    <div className="space-y-1">
                      <p className="text-[11px] font-bold text-slate-600">Password</p>
                      <input 
                        id="paypal-checkout-password"
                        type="password"
                        placeholder="••••••••"
                        value={paypalPassword}
                        onChange={(e) => setPaypalPassword(e.target.value)}
                        className="w-full text-xs p-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-[#003087] outline-none transition-all"
                      />
                    </div>

                    <div className="flex items-center gap-2 pt-1 text-[10px] text-slate-500 font-normal">
                      <Lock className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                      <span>This is a secure simulated environment. Your actual password will never be stored or logged.</span>
                    </div>

                    <div className="pt-2 flex gap-2">
                      <button 
                        onClick={() => setSelectedPlanForCheckout(null)}
                        className="flex-1 py-3 text-xs font-bold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl cursor-pointer"
                      >
                        Cancel
                      </button>
                      <button 
                        id="btn-paypal-login"
                        onClick={executeSimulatedPayment}
                        className="flex-1 py-3 text-xs font-bold text-white bg-[#0079C1] hover:bg-[#005ea6] rounded-xl cursor-pointer shadow-sm text-center"
                      >
                        Log In & Authorize Pay
                      </button>
                    </div>
                  </div>
                )}

                {checkoutStep === 'paypal_processing' && (
                  <div className="py-10 flex flex-col items-center justify-center space-y-4">
                    <Loader2 className="w-10 h-10 text-[#003087] animate-spin" />
                    <div className="text-center">
                      <h4 className="text-xs font-extrabold text-slate-900">Contacting PayPal Servers...</h4>
                      <p className="text-[10px] text-slate-400 mt-1 font-normal">Routing secure escrow clearance and executing membership assignment...</p>
                    </div>
                  </div>
                )}

                {checkoutStep === 'success' && (
                  <div className="space-y-5 text-center">
                    <div className="w-12 h-12 bg-emerald-50 text-emerald-600 border border-emerald-100 rounded-full flex items-center justify-center mx-auto">
                      <Check className="w-6 h-6" />
                    </div>

                    <div className="space-y-1">
                      <h4 className="text-sm font-bold text-[#0A0B10]">Payment Authorized & Subscription Active!</h4>
                      <p className="text-[11px] text-slate-500">PayPal ID: <span className="font-mono text-slate-700">TX-PAYPAL-774021X</span></p>
                    </div>

                    <div className="p-3.5 bg-emerald-50/50 border border-emerald-100 rounded-xl text-left space-y-1.5 text-[11px] text-emerald-950 font-normal">
                      <p className="font-bold flex justify-between">
                        <span>Paid To:</span>
                        <span>RFXpress SaaS Inc.</span>
                      </p>
                      <p className="font-bold flex justify-between">
                        <span>Cleared Via:</span>
                        <span>{paypalEmail || 'PayPal Wallet Balance'}</span>
                      </p>
                      <p className="font-bold flex justify-between">
                        <span>Escrow Receipt:</span>
                        <span>#SAAS-{Math.floor(100000 + Math.random() * 900000)}</span>
                      </p>
                    </div>

                    <button 
                      id="btn-finalize-upgrade"
                      onClick={finalizeUpgradeAfterSuccess}
                      className="w-full py-3.5 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl cursor-pointer shadow-sm"
                    >
                      Authenticate Google & Access Dashboard
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
};
